// @flow
import dbg from 'debug';
import { waitInWindow } from '@justpark/helpers/src/scriptLoader/scriptLoader';
import type { Ampli } from '../../generated/ampli/index.flow';
import config from '../../../config/config';
import { selectIsWebview } from '../../stores/ui';

const debug = dbg('jp:amplitude');

const handler = () => {
  if (__SERVER__) {
    return {
      get() {
        return () => {
          // Amplitude tracking is a noop during server side rendering
        };
      }
    };
  }

  const ampliImport = import('../../generated/ampli/index');

  let resolveLoad;
  const loadPromise = new Promise((resolve) => {
    resolveLoad = resolve;
  });

  return {
    get(target, prop) {
      if (prop === 'client') {
        return {
          add: (...args) =>
            ampliImport.then((ampliModule) =>
              ampliModule.ampli.amplitude.add(...args)
            )
        };
      }

      return (...args) =>
        ampliImport.then(({ ampli }) => {
          if (prop === 'load') {
            const result = ampli[prop](...args);
            resolveLoad();

            return result;
          }

          return loadPromise.then(() => ampli[prop](...args));
        });
    }
  };
};

export const ampli: Ampli = new Proxy({}, handler());

const waitForCookiePolicy = () =>
  new Promise((resolve) => {
    if (window.Cypress) {
      debug(
        'Cookie banner not loaded or not enabled, so ready to load analytics'
      );
      resolve();

      return;
    }

    waitInWindow('OptanonActiveGroups').then(() => {
      if (document.cookie.indexOf('OptanonAlertBoxClosed=') >= 0) {
        // Optanon cookie found, use has either accepted or rejected cookies
        debug('Cookie found, so ready to load analytics');
        resolve();
      }

      function OptanonWrapper() {
        // eslint-disable-next-line no-undef,new-cap
        Optanon.OnConsentChanged(() => {
          debug('Cookie changed, so ready to load analytics');
          resolve();
        });
      }

      window.OptanonWrapper = OptanonWrapper;
    });
  });

class PropertiesEnrichmentPlugin {
  name = 'properties-plugin';

  type = 'enrichment';

  constructor(state) {
    this.state = state;
  }

  async setup() {
    const userId = this.state?.auth?.user?.id;
    const properties = [];

    if (this.state.launchDarkly.ids.length > 0) {
      this.state.launchDarkly.ids.forEach((id) => {
        const feature = this.state.launchDarkly.entities[id];
        if (typeof feature.value !== 'object') {
          properties.push(`${id}.${feature.value}`);
        }
      });
    }

    ampli.identify(userId, {
      launch_darkly: properties
    });

    return undefined;
  }

  // eslint-disable-next-line class-methods-use-this
  async execute(event) {
    return event;
  }
}

const ampliInit = (reduxState) => {
  const isWebview = selectIsWebview(reduxState);

  const cookieInitOrSkip = () => {
    if (isWebview) {
      return Promise.resolve(true);
    }

    return waitForCookiePolicy().then(() => {
      const groupsArray = window.OptanonActiveGroups.split(',');
      const performanceCookiesGroup = 'C0002';
      return groupsArray.indexOf(performanceCookiesGroup) > -1;
    });
  };

  cookieInitOrSkip().then((optIn) => {
    if (optIn) {
      ampli.load({
        environment: config.isProduction ? 'production' : 'staging',
        client: {
          configuration: {
            defaultTracking: {
              formInteractions: false,
              fileDownloads: false
            }
          }
        }
      });

      ampli.client.add(new PropertiesEnrichmentPlugin(reduxState));
    }
  });
};

export default ampliInit;
